import React, { useState, useEffect } from "react";
import { Tree, Form } from "antd";
import type { DataNode, TreeProps } from "antd/es/tree";
import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
import { useDispatch, useSelector } from "react-redux";
import centralApi from "services/centralApi";
import ShowMessage from "components/ShowMessage/ShowMessage";
import Spinner from "components/Spinner/Spinner";
import { t } from "i18next";
import { API_ENDPOINTS } from "utils/constants";
import { getToken } from "redux/features/app/authTokenSlice";
import "./ConfigureColumns.css";
import useMessage from "hooks/useMessage";

interface ConfigureColumnsProps {
  currentAppKey: any
  updateCurrentAppKey: () => void
}

const ConfigureColumns: React.FC<ConfigureColumnsProps> = ({
  currentAppKey,
  updateCurrentAppKey,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const authToken = useSelector((state: any) => state.authToken.token);
  const loginUser = useSelector((state: any) => state.loginUser.data.api_key);
  const activeApp = useSelector((state: any) => state.activeApp.appId);
  const { showError, showSuccess } = useMessage();

  const allCampaignColumns = currentAppKey[0]?.campaign_columns || {};
  const allowedItems = currentAppKey[0]?.allowedItems || [];

  const [loading, setLoading] = useState(false);
  const [columnTreeData, setColumnTreeData] = useState<DataNode[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);

  const defaultColumns: DataNode[] = [
    { key: "nm", title: t("campaignDetailsLabel") },
    { key: "t", title: t("typeLabel") },
    { key: "segmentName", title: t("audienceLabel") },
    { key: "reach", title: t("reachableLabel") },
    { key: "pushedTo", title: t("sentLabel") },
    { key: "receivedTo", title: t("receivedLabel") },
    { key: "impressionRate", title: t("impressionRateLabel") },
    { key: "clickedTo", title: t("clickedLabel") },
    { key: "ctr", title: t("CTRLabel") },
    { key: "conversion", title: t("conversionLabel") },
    { key: "sd", title: t("startDateLabel") },
    { key: "ed", title: t("endDateLabel") },
    { key: "totalCost", title: t("totalCostLabel") }, 
    { key: "costPerClick", title: t("costPerClickLabel") }, 
    { key: "costPerConversion", title: t("costPerConversionLabel") },
    { key: "actions", title: t("actionLabel") }, 


  ];
  

  useEffect(() => {
    const defaultColumnKeys = defaultColumns.map((col) => col.key);
  
    // Use default order if no columnOrder is defined
    const savedOrder = currentAppKey?.[0]?.columnOrder ?? [];
  
    // Merge saved order with any new default columns (preserves previous order)
    const initialOrder: string[] = [
      ...savedOrder,
      ...defaultColumnKeys.filter((key) => !savedOrder.includes(key)),
    ];
  
    // If campaign_columns is missing, assume all default columns are visible
    const campaignColumns = currentAppKey?.[0]?.campaign_columns ?? {};
    const hasVisibilityConfig = Object.keys(campaignColumns).length > 0;
  
    const selectedColumnKeys = hasVisibilityConfig
      ? Object.entries(campaignColumns)
          .filter(([_, visible]) => visible)
          .map(([key]) => key)
      : [...defaultColumnKeys]; // default all to visible
  
    const orderedData: DataNode[] = initialOrder.map((key) => {
      const found = defaultColumns.find((col) => col.key === key);
      return (
        found || {
          key,
          title: key, // fallback title if missing
        }
      );
    });
  
    setColumnTreeData(orderedData);
    setCheckedKeys(selectedColumnKeys);
  }, [currentAppKey]);
  
  const handleCheck: TreeProps["onCheck"] = (checkedKeysValue) => {
    setCheckedKeys(checkedKeysValue as string[]);
  };

  const handleDrop: TreeProps["onDrop"] = (info) => {
    const dragKey = info.dragNode.key;
    const dropKey = info.node.key;
    const dropPosition = info.dropPosition;
    const dropToGap = info.dropToGap;

    const data = [...columnTreeData];

    // Find drag object
    const dragIndex = data.findIndex((item) => item.key === dragKey);
    const dragObj = data.splice(dragIndex, 1)[0];

    // Find drop index
    const dropIndex = data.findIndex((item) => item.key === dropKey);

    if (dropToGap) {
      const insertIndex = dropPosition > dragIndex ? dropIndex + 1 : dropIndex;
      data.splice(insertIndex, 0, dragObj);
    } else {
      data.splice(dropIndex, 0, dragObj);
    }

    setColumnTreeData(data);
  };

  const onFormSubmit = async () => {

    const allKeys = columnTreeData.map((item) => item.key);
const selectedColumns: Record<string, boolean> = {};

allKeys.forEach((key) => {
  selectedColumns[key] = checkedKeys.includes(key);
});


    const columnOrder = columnTreeData.map((item) => item.key);


    const payload = {
      args: JSON.stringify({
        app_id: activeApp,
        selectedColumns,
        columnOrder,
        allowedItems,
      }),
      api_key: loginUser,
      authToken,
    };

    try {
      setLoading(true);
      await centralApi("POST", API_ENDPOINTS.SAVE_CAMPAIGN_SETTINGS_URL, payload, null);
      showSuccess(t("sucessfullySavedLabel"));
      updateCurrentAppKey();
    } catch (err) {
      showError(t("somethingWrongLabel"));
    } finally {
      setLoading(false);
      dispatch(getToken());
    }
  };

  return (
    <div className="configure-columns-setting-container">
      <h6 className="configure-columns-setting-container-heading">{t("campaignColumnsLabel")}</h6>
      <p className="configure-columns-setting-form-headings">{t("appSettingColumnsAnalyticsDes")}</p>

      {loading ? (
        <Spinner />
      ) : (
        <Form form={form} onFinish={onFormSubmit}>
      
          <Tree
  checkable
  draggable
  blockNode // makes entire row draggable
  onDrop={handleDrop}
  onCheck={handleCheck}
  checkedKeys={checkedKeys}
  treeData={columnTreeData}
/>

          <PrimaryButton
            size="middle"
            type="primary"
            htmlType="submit"
            className="configure-columns-btn-save"
          >
            {t("saveLabel")}
          </PrimaryButton>
        </Form>
      )}
    </div>
  );
};

export default ConfigureColumns;
